import { VirtualDevice, type SimulationContext } from '../VirtualDevice';
import { getAnalogConfig, scaleEngToRaw, scaleRawToEng } from '../analog';

export interface TankState extends Record<string, unknown> {
  capacity: number;
  level: number;
  inletFlowRate: number;
  outletFlowRate: number;
  levelPercent: number;
  isEmpty: boolean;
  isFull: boolean;
}

export class Tank extends VirtualDevice<TankState> {

  override reset() {
    this.state.capacity = Number(this.props['capacity'] || 1000);
    this.state.level = Math.max(0, Math.min(this.state.capacity, Number(this.props['initialLevel'] || 0)));
    this.state.inletFlowRate = 0;
    this.state.outletFlowRate = 0;
    this.state.levelPercent = this.state.capacity > 0 ? (this.state.level / this.state.capacity) * 100 : 0;
    this.state.isEmpty = this.state.level <= 0;
    this.state.isFull = this.state.level >= this.state.capacity;
  }

  override update(context: SimulationContext) {
    // Flow accumulation and physics are done by active components (Pump, Valve)
    // to avoid complex topological sorting.
    // Tank only clamps its level and updates percent.
    this.state.level = Math.max(0, Math.min(this.state.capacity, this.state.level));
    this.state.levelPercent = this.state.capacity > 0 ? (this.state.level / this.state.capacity) * 100 : 0;
    this.state.isEmpty = this.state.level <= 0;
    this.state.isFull = this.state.level >= this.state.capacity;

    const lowLimit = Number(this.props['lowLevelAlarm'] ?? 10);
    const highLimit = Number(this.props['highLevelAlarm'] ?? 90);
    
    context.alarms?.evaluateCondition(
      this.state.levelPercent <= lowLimit,
      this.id,
      'TANK_LEVEL_LOW',
      'Tank Level Low',
      `Tank level is at or below ${lowLimit}%`,
      'WARNING',
      context.simulationTimeMs,
      this.state.levelPercent,
      lowLimit
    );

    context.alarms?.evaluateCondition(
      this.state.levelPercent >= highLimit,
      this.id,
      'TANK_LEVEL_HIGH',
      'Tank Level High',
      `Tank level is at or above ${highLimit}%`,
      'WARNING',
      context.simulationTimeMs,
      this.state.levelPercent,
      highLimit
    );
  }
}

export interface PumpState extends Record<string, unknown> {
  running: boolean;
  actualFlow: number;
  analogControlValue?: number;
  signalStatus?: string;
}

export class Pump extends VirtualDevice<PumpState> {

  override reset() {
    this.state.running = false;
    this.state.actualFlow = 0;
    delete this.state.analogControlValue;
    this.state.signalStatus = 'NORMAL';
  }

  override update(context: SimulationContext) {
    let desiredFlow: number;
    const nominalFlowRate = Number(this.props['nominalFlowRate'] || 50);
    const analogControl = this.state.analogControlValue;

    if (analogControl !== undefined) {
      // Proportional control directly injected (e.g. from PID)
      const clamped = Math.max(0, Math.min(100, analogControl));
      desiredFlow = (clamped / 100) * nominalFlowRate;
      this.state.running = desiredFlow > 0;
    } else if (this.isAnalogAddress()) {
      // Analog control from PLC QW
      const raw = this.readPlcAnalogOutput(context.host);
      const config = getAnalogConfig(this.props, 0, nominalFlowRate);
      const result = scaleRawToEng(raw, config);
      this.state.signalStatus = result.status;
      
      context.alarms.evaluateCondition(
        result.status === 'WIRE_BREAK',
        this.id,
        'WIRE_BREAK',
        'Control Signal Lost',
        'Analog signal dropped below minimum safe threshold',
        'CRITICAL',
        context.simulationTimeMs
      );

      if (result.status === 'WIRE_BREAK' || result.status === 'INVALID') {
        desiredFlow = 0;
        this.state.running = false;
      } else {
        // Clamp to physical limits
        desiredFlow = Math.max(0, Math.min(nominalFlowRate, result.eng));
        this.state.running = desiredFlow > 0;
      }
    } else {
      // Digital control
      const isRunning = this.readPlcOutput(context.host);
      this.state.running = isRunning;
      desiredFlow = isRunning ? nominalFlowRate : 0;
      this.state.signalStatus = 'NORMAL';
    }

    let sourceId = this.props['sourceId'] as string;
    let targetId = this.props['targetId'] as string;

    // Prevent circular deadlock if pump was accidentally linked to same tank for source and target
    if (sourceId && sourceId === targetId) {
      sourceId = '';
    }

    // Fallback: If targetId is not specified, check if there is a single tank in the simulation process
    if (!targetId && context.process) {
      const tanks = Array.from(context.process.devices.values()).filter((d) => d.type === 'tank') as Tank[];
      const firstTank = tanks[0];
      if (tanks.length === 1 && firstTank && firstTank.id !== sourceId) {
        targetId = firstTank.id;
      }
    }
    
    let sourceValid = true;
    let targetValid = true;

    // Check constraints
    if (sourceId ) {
      const source = context.process.getDevice(sourceId);
      if (source && source.type === 'tank') {
        const sourceTank = source as Tank;
        if (sourceTank.state.level <= 0) desiredFlow = 0;
      } else {
        sourceValid = false;
      }
    }
    if (targetId ) {
      const target = context.process.getDevice(targetId);
      if (target && target.type === 'tank') {
        const targetTank = target as Tank;
        if (targetTank.state.level >= targetTank.state.capacity) desiredFlow = 0;
      } else {
        targetValid = false;
      }
    }

    context.alarms?.evaluateCondition(
      Boolean((sourceId && !sourceValid) || (targetId && !targetValid)),
      this.id,
      'INVALID_CONNECTION',
      'Invalid Connection',
      'The source or target tank is not found or is invalid.',
      'ERROR',
      context.simulationTimeMs
    );

    // Apply flow volume
    if (desiredFlow > 0) {
      const dt = context.deltaTimeMs / 1000;
      if (dt > 0) {
        let actualVolume = desiredFlow * dt;
        let sourceTank: Tank | undefined, targetTank: Tank | undefined;

        if (sourceId) {
          const dev = context.process.getDevice(sourceId);
          if (dev && dev.type === 'tank') sourceTank = dev as Tank;
        }
        if (targetId) {
          const dev = context.process.getDevice(targetId);
          if (dev && dev.type === 'tank') targetTank = dev as Tank;
        }

        if (sourceTank) {
          actualVolume = Math.min(actualVolume, sourceTank.state.level);
        }
        if (targetTank) {
          actualVolume = Math.min(actualVolume, targetTank.state.capacity - targetTank.state.level);
        }

        this.state.actualFlow = actualVolume / dt;

        if (sourceTank) {
          sourceTank.state.level -= actualVolume;
          sourceTank.state.outletFlowRate += this.state.actualFlow;
        }
        if (targetTank) {
          targetTank.state.level += actualVolume;
          targetTank.state.inletFlowRate += this.state.actualFlow;
        }
      } else {
        this.state.actualFlow = desiredFlow;
      }
    } else {
      this.state.actualFlow = 0;
    }
  }
}

export interface ValveState extends Record<string, unknown> {
  open: boolean;
  position: number; // 0 to 100
  actualFlow: number;
  analogControlValue?: number;
  signalStatus?: string;
}

export class Valve extends VirtualDevice<ValveState> {

  override reset() {
    this.state.open = false;
    this.state.position = 0;
    this.state.actualFlow = 0;
    delete this.state.analogControlValue;
    this.state.signalStatus = 'NORMAL';
  }

  override update(context: SimulationContext) {
    let desiredFlow: number;
    const maxFlowRate = Number(this.props['maxFlowRate'] || 50);
    const analogControl = this.state.analogControlValue;

    if (analogControl !== undefined) {
      // Proportional control directly injected
      const clamped = Math.max(0, Math.min(100, analogControl));
      desiredFlow = (clamped / 100) * maxFlowRate;
      this.state.open = desiredFlow > 0;
    } else if (this.isAnalogAddress()) {
      // Analog control from PLC
      const raw = this.readPlcAnalogOutput(context.host);
      const config = getAnalogConfig(this.props, 0, maxFlowRate);
      const result = scaleRawToEng(raw, config);
      this.state.signalStatus = result.status;
      
      context.alarms.evaluateCondition(
        result.status === 'WIRE_BREAK',
        this.id,
        'WIRE_BREAK',
        'Control Signal Lost',
        'Analog signal dropped below minimum safe threshold',
        'CRITICAL',
        context.simulationTimeMs
      );

      if (result.status === 'WIRE_BREAK' || result.status === 'INVALID') {
        desiredFlow = 0;
        this.state.open = false;
      } else {
        desiredFlow = Math.max(0, Math.min(maxFlowRate, result.eng));
        this.state.open = desiredFlow > 0;
      }
    } else {
      // Digital control
      const isEnergized = this.readPlcOutput(context.host);
      const isNormallyOpen = this.props['valveType'] === 'NO';
      const isOpen = isNormallyOpen ? !isEnergized : isEnergized;
      this.state.open = isOpen;
      desiredFlow = isOpen ? maxFlowRate : 0;
      this.state.signalStatus = 'NORMAL';
    }

    let sourceId = this.props['sourceId'] as string;
    let targetId = this.props['targetId'] as string;

    // Prevent circular deadlock if valve was accidentally linked to same tank for source and target
    if (sourceId && sourceId === targetId) {
      sourceId = '';
    }

    // Fallback: If targetId is not specified, check if there is a single tank in the simulation process
    if (!targetId && context.process) {
      const tanks = Array.from(context.process.devices.values()).filter((d) => d.type === 'tank') as Tank[];
      const firstTank = tanks[0];
      if (tanks.length === 1 && firstTank && firstTank.id !== sourceId) {
        targetId = firstTank.id;
      }
    }
    
    let sourceValid = true;
    let targetValid = true;

    // Check constraints
    if (sourceId ) {
      const source = context.process.getDevice(sourceId);
      if (source && source.type === 'tank') {
        const sourceTank = source as Tank;
        if (sourceTank.state.level <= 0) desiredFlow = 0;
      } else {
        sourceValid = false;
      }
    }
    if (targetId ) {
      const target = context.process.getDevice(targetId);
      if (target && target.type === 'tank') {
        const targetTank = target as Tank;
        if (targetTank.state.level >= targetTank.state.capacity) desiredFlow = 0;
      } else {
        targetValid = false;
      }
    }

    context.alarms?.evaluateCondition(
      Boolean((sourceId && !sourceValid) || (targetId && !targetValid)),
      this.id,
      'INVALID_CONNECTION',
      'Invalid Connection',
      'The source or target tank is not found or is invalid.',
      'ERROR',
      context.simulationTimeMs
    );

    // Apply flow volume
    if (desiredFlow > 0) {
      const dt = context.deltaTimeMs / 1000;
      if (dt > 0) {
        let actualVolume = desiredFlow * dt;
        let sourceTank: Tank | undefined, targetTank: Tank | undefined;

        if (sourceId) {
          const dev = context.process.getDevice(sourceId);
          if (dev && dev.type === 'tank') sourceTank = dev as Tank;
        }
        if (targetId) {
          const dev = context.process.getDevice(targetId);
          if (dev && dev.type === 'tank') targetTank = dev as Tank;
        }

        if (sourceTank) {
          actualVolume = Math.min(actualVolume, sourceTank.state.level);
        }
        if (targetTank) {
          actualVolume = Math.min(actualVolume, targetTank.state.capacity - targetTank.state.level);
        }

        this.state.actualFlow = actualVolume / dt;

        if (sourceTank) {
          sourceTank.state.level -= actualVolume;
          sourceTank.state.outletFlowRate += this.state.actualFlow;
        }
        if (targetTank) {
          targetTank.state.level += actualVolume;
          targetTank.state.inletFlowRate += this.state.actualFlow;
        }
      } else {
        this.state.actualFlow = desiredFlow;
      }
    } else {
      this.state.actualFlow = 0;
    }
  }
}

export interface LevelSensorState extends Record<string, unknown> {
  level: number;
  scaledValue: number;
  outputValue: number;
  signalStatus: string;
  faulted?: boolean;
}

export class LevelSensor extends VirtualDevice<LevelSensorState> {

  override reset() {
    this.state.level = 0; // The physical level
    this.state.scaledValue = 0; // The engineering unit reported
    this.state.outputValue = 0; // The raw PLC value
    this.state.signalStatus = 'NORMAL';
    this.state.faulted = false;
  }

  override update(context: SimulationContext) {
    const tankId = this.props['tankId'] as string;
    let currentLevel = 0;

    if (tankId ) {
      const tank = context.process.getDevice(tankId);
      if (tank && tank.type === 'tank') {
        const t = tank as Tank;
        currentLevel = t.state.level;
      }
    }

    this.state.level = currentLevel;

    const minLevel = Number(this.props['minLevel'] || 0);
    const maxLevel = Number(this.props['maxLevel'] || 1000);
    const config = getAnalogConfig(this.props, minLevel, maxLevel);

    // Physical faults can be injected here. E.g. broken sensor.
    const isWireBreak = this.state.faulted === true; // Simplified fault injection

    const result = scaleEngToRaw(currentLevel, config, isWireBreak);
    
    this.state.outputValue = result.raw;
    this.state.scaledValue = result.eng;
    this.state.signalStatus = result.status;

    context.alarms?.evaluateCondition(
      result.status === 'WIRE_BREAK',
      this.id,
      'WIRE_BREAK',
      'Sensor Wire Break',
      'Analog input dropped below minimum safe threshold',
      'CRITICAL',
      context.simulationTimeMs
    );

    context.alarms?.evaluateCondition(
      result.status === 'OVER_RANGE',
      this.id,
      'OVER_RANGE',
      'Sensor Over Range',
      'Measured value exceeds maximum scaled limit',
      'WARNING',
      context.simulationTimeMs
    );

    context.alarms?.evaluateCondition(
      result.status === 'UNDER_RANGE',
      this.id,
      'UNDER_RANGE',
      'Sensor Under Range',
      'Measured value is below minimum scaled limit',
      'WARNING',
      context.simulationTimeMs
    );

    // Write to PLC Input Word (IW) if connected
    if (this.isAnalogAddress()) {
      this.writePlcAnalogInput(context.host, this.state.outputValue);
    }
  }
}
